import $ from 'jquery';

const CONFIG = {
    OPEN: '.js-modalOpen',
    CLOSE: '.js-modalClose',
    CONTAINER: '.modal',
    CONTENT: '.modal-content',
    ACTIONS: '.modal-actions',
    BODY_SELECTOR: 'modal-isActive',
    DATA_ID: 'data-modal-id',
    DATA_ID_JS: 'modalId',
    TRANSITION_LENGTH: 150
};

/**
 * @fileOverview View handles updating the visual elements on the page
 */
export default class ModalView {
    /**
     * Construct the view and run initialization
     *
     * @param element
     */
    constructor(element) {
        /**
         * Check to see if elements are passed in as jQuery objects or strings
         */
        this.$element = (element instanceof $) ? element : $(element);

        /**
         * If element doesn't exist, exit early
         */
        if (!this.$element.length) {
            return;
        }

        this.init();
    }

    /**
     * Initialize the view
     *
     * @method init
     */
    init() {
        this.isEnabled = false;
        this.isActive = false;
        this.$activeElement = null;
        this.$lastElement = $(document);

        return this.setupHandlers()
                   .createChildren()
                   .layout()
                   .enable();
    }

    /**
     * Binds the scope of handler functions
     * Should be run once on view initialization
     *
     * @method setupHandlers
     * @returns {ModalView}
     */
    setupHandlers() {
        this.handleOpenClick = this.onOpenClick.bind(this);
        this.handleCloseClick = this.onCloseClick.bind(this);
        this.handleKeydown = this.onKeydown.bind(this);
        this.handleTabFirst = this.onTabFirst.bind(this);
        this.handleTabLast = this.onTabLast.bind(this);

        return this;
    }

    /**
     * Create any child objects and references to DOM elements
     * Should only be run once on init of the view
     *
     * @method createChildren
     * @returns {ModalView}
     */
    createChildren() {
        this.$open = $(CONFIG.OPEN);
        this.$close = this.$element.find(CONFIG.CLOSE);

        for (let i = 0; i < this.$element.length; i++) {
            if ($(this.$element[i]).data('modalLaunch')) {
                this.launchOnLoad($(this.$element[i]));
            }
        }

        return this;
    }

    /**
     * Update the DOM to reflect the current state of the model
     *
     * @method layout
     * @returns {ModalView}
     */
    layout() {
        return this;
    }

    /**
     * Disable, update the DOM, and then re-enable to create handlers for new DOM
     *
     * @method render
     */
    render() {
        this.disable();
        this.layout();
        this.enable();
    }

    /**
     * Enables the view
     * Performs binding to handlers
     * Exits early if already enabled
     *
     * @method enable
     * @returns {ModalView}
     */
    enable() {
        if (this.isEnabled) {
            return this;
        }

        this.isEnabled = true;

        for (let i = 0; i < this.$open.length; i++) {
            this.$open[i].addEventListener('click', this.handleOpenClick);
        }

        for (let i = 0; i < this.$close.length; i++) {
            this.$close[i].addEventListener('click', this.handleCloseClick);
        }

        document.addEventListener('keydown', this.handleKeydown);

        return this;
    }

    /**
     * Disables the view
     * Tears down any event binding to handlers
     * Exits early if it is already disabled
     *
     * @method disable
     * @returns {ModalView}
     */
    disable() {
        if (!this.isEnabled) {
            return this;
        }

        for (let i = 0; i < this.$open.length; i++) {
            this.$open[i].removeEventListener('click', this.handleOpenClick);
        }

        for (let i = 0; i < this.$close.length; i++) {
            this.$close[i].removeEventListener('click', this.handleCloseClick);
        }

        document.removeEventListener('keydown', this.handleKeydown);

        this.isEnabled = false;

        return this;
    }

    /**
     * Destroys the view
     * Tears down any events, handlers, elements
     * Should be called when the object should be left unused
     *
     * @method destroy
     * @returns {ModalView}
     */
    destroy() {
        this.disable();

        return this;
    }

    /**
     * Open the modal on page load if data element is present
     *
     * @param $element
     * @returns {ModalView}
     */
    launchOnLoad($element) {
        if (($element.data('modalLaunch') >= $(window).width()) || ($element.data('modalLaunch') === -1)) {
            this.openModal($element);
        }

        return this;
    }

    /**
     * Handles open click event
     *
     * @param event
     * @returns {ModalView}
     */
    onOpenClick(event) {
        const $target = $(event.currentTarget);
        const itemId = $target.attr('id');
        if (this.isActive === false) {
            this.saveFocus($target);
            this.openModal($(`#${$(event.currentTarget).data(CONFIG.DATA_ID_JS)}`), itemId);
        }

        return this;
    }

    /**
     * Handles close click event
     *
     * @param event
     * @returns {ModalView}
     */
    onCloseClick(event) {
        if (this.isActive === true) {
            this.closeModal($(event.currentTarget).closest(this.$element));
            this.$inputFirst.off('keydown', this.handleTabFirst);
            this.$inputLast.off('keydown', this.handleTabLast);
            this.resetFocus(this.$lastElement);
        }

        return this;
    }

    /**
     * Handles keydown event
     * Close modal if user hits ESC
     *
     * @param event
     * @returns {ModalView}
     */
    onKeydown(event) {
        if ((this.isActive === true) && ((!event.keyCode) || (event.keyCode === 27))) {
            this.closeModal(this.$activeElement);
            this.resetFocus(this.$lastElement);
        }

        return this;
    }

    /**
     * Handles shift+tab keydowns on the first focusable element
     * Sends focus back to last element in modal
     *
     * @returns {ModalView}
     */
    onTabFirst() {
        if (event.which === 9 && event.shiftKey) {
            event.preventDefault();
            this.$inputLast.focus();
        }

        return this;
    }

    /**
     * Handles tab keydowns on the last focusable element
     * Sends focus back to first element in modal
     *
     * @returns {ModalView}
     */
    onTabLast() {
        if (event.which === 9 && !event.shiftKey) {
            event.preventDefault();
            this.$inputFirst.focus();
        }

        return this;
    }

    /**
     * Opens modal
     * Configure <body> so it will not scroll behind modal
     * Fade in modal and make sure it has focus/tabindex for screen readers
     *
     * @param {jQuery|HTMLElement} $modal
     * @param {string} itemId
     * @returns {ModalView}
     */
    openModal($modal, itemId) {
        const $body = $('body');
        const $modalContent = $modal.find(CONFIG.CONTENT);
        const scrollPos = $body.scrollTop() * -1;

        $body.addClass(CONFIG.BODY_SELECTOR).css({top: scrollPos});
        $modal.fadeIn(CONFIG.TRANSITION_LENGTH).attr('aria-hidden','false');

        this.$inputs = this.$element.find('select, input, textarea, button, a').filter(':visible');
        this.$inputFirst = this.$inputs.first();
        this.$inputLast = this.$inputs.last();
        this.$inputFirst.on('keydown', this.handleTabFirst);
        this.$inputLast.on('keydown', this.handleTabLast);

        itemId ? $modal.find('.js-modal-includeId').attr('id', itemId) : '';

        setTimeout(() => {
            this.$inputFirst.focus();
        }, CONFIG.TRANSITION_LENGTH);

        this.sizeAndPosition($modal);

        this.isActive = true;
        this.$activeElement = $modal;

        return this;
    }

    /**
     * Closes modal
     * Reset body to default
     * Set focus to previously focused element
     *
     * @param {jQuery|HTMLElement} $modal
     * @returns {ModalView}
     */
    closeModal($modal) {
        const $body = $('body');
        const $modalContent = $modal.find(CONFIG.CONTENT);
        const scrollPos = parseInt($body.css('top')) * -1;

        $modal.fadeOut(CONFIG.TRANSITION_LENGTH).attr('aria-hidden','true');
        $body.removeClass(CONFIG.BODY_SELECTOR).css({top: ''}).scrollTop(scrollPos);
        $modalContent.attr('tabindex', '-1');
        this.$lastElement.focus();

        this.isActive = false;
        this.$activeElement = null;

        return this;
    }

    /**
     * Determine height and positioning of modal based upon window size
     *
     * @param $modal
     * @returns {ModalView}
     */
    sizeAndPosition($modal) {
        const $modalContainer = $modal.find(CONFIG.CONTAINER);
        const $modalContent = $modal.find(CONFIG.CONTENT);
        const $modalActions = $modal.find(CONFIG.ACTIONS);

        if ($modalContainer.height() > $(window).height()) {

            const borders = parseInt($modalContent.css('padding-top'))
                + parseInt($modalContent.css('padding-bottom'))
                + parseInt($modalContainer.css('margin-top'))
                + parseInt($modalContainer.css('margin-bottom'))
                + parseInt($modalContainer.css('top'))
                + parseInt($modalContainer.css('top'));
            const fitHeight = $(window).height() - $modalActions.outerHeight() - borders;
            $modalContent.css('height', `${fitHeight}px`);
        } else if ($(window).width() >= 768) {
            $modalContainer.css('top', `calc((100vh - ${$modalContainer.outerHeight()}px) * .25)`);
        }

        return this;
    }

    /**
     * Save the element that was focused before modal was opened
     *
     * @param $target
     * @returns {ModalView}
     */
    saveFocus($target) {
        this.$lastElement = $target; // Save last element of focus to return to after modal close

        return this;
    }

    /**
     * Return focus to previously focused element
     *
     * @param $target
     * @returns {ModalView}
     */
    resetFocus($target) {
        $target.focus();

        return this;
    }
}
